// @writing-suite/core: the Five Rooms model in TypeScript, ported from SuiteModel (see each module's header).
export type * from './contract.ts';

// The contract's implementations.
export { openProject } from './project.ts';
export { prose } from './proseMarkdown.ts';
export { fountain } from './fountain.ts';

// The project, beyond the contract.
export { open, entityKindOf, isNumberedChapter, StandardField, type OpenedProject } from './project.ts';
export type {
  Arrangement as StoredArrangement, ArrangementNode, CheckpointRecord, FragmentRecord, Issue, MetadataField, ProjectRecord,
  StoryEntity, Suggestion, WritingTargets,
} from './model/documents.ts';
export { DISTANT_PAST, readFragment, readProject, settingJSON } from './model/documents.ts';
export { ancestors, flattened, orderedFragmentIDs, resolvedParent, tree as arrangementTree, type ArrangementTreeNode } from './model/arrangement.ts';
export { counted, scenes, MARKDOWN_KINDS, type Scene, type SelectionEntry } from './model/manuscriptSelection.ts';
export { standardCompare, swiftCompare } from './model/order.ts';
export { FAR_FROM_TARGET, isFarFromTarget, manuscriptStarts, POSITION_MINIMUM_WORDS } from './model/positions.ts';
// Script formats: a screenplay, or a stage play in US or UK format.
export {
  isMusicalOf, isPoetryOf, isScriptKind, isStageFormat, isUnsupportedFormat, MADE_MANUSCRIPT_NAMES, POETRY_TEMPLATE_ID, PROJECT_KINDS, projectDisplayName, projectKind,
  projectKindName, scriptFamily, SCRIPT_FORMATS, scriptFormatName, scriptFormatOf, scriptManuscriptName, stageFormatForRegion,
  type ProjectKind, type ScriptFamily, type ScriptFormat,
} from './scriptFormat.ts';
// What agents are told about writing into a project (MCP describe_project).
export { agentConventions, agentFormatLine, writtenIn } from './agentConventions.ts';
export { AgentBrief, isAgentBrief } from './agentBrief.ts';
export { AGENT_DRAFT_FIELD, agentDraftHash, agentDraftMarker, readAgentDraft, type AgentDraftRecord } from './agentDraft.ts';
// Locks against accidental change.
export { effectiveLocks, LockedError } from './locks.ts';
// Maps: notes on a sheet of paper joined by lines, kept in Research & Notes (Maps.swift, MapSketch.swift).
export {
  emptyMapContent, lineJoins, MAP_ARROWS, MAP_AUTO_WIDTH, MAP_FACES, MAP_LINE_STYLES, MAP_NOTE_SIZES, MAP_OUTLINES, MapChange, MapKey, MapLink,
  mapLine, mapNeighbours, mapNote, mapNoteOf, MapNumber, mapOutline, mapPlainText, mapRegion, Maps, mapStroke, placedStroke, readMapContent, sameMapContent,
  type MapArrow, type MapContent, type MapFace, type MapLine, type MapLineProperty, type MapLineStyle, type MapNote, type MapNoteProperty,
  type MapNoteSize, type MapOutline, type MapPoint, type MapRegion, type MapStroke, type MapStrokePoint,
} from './maps/map.ts';
export { MapDocument, writeMapKeys } from './maps/mapDocument.ts';
export { MapLayout, type MapDirection, type MapFrame, type MapFrames } from './maps/layout.ts';
export { MapSketch, type SketchMark, type SketchPoint, type SketchStyle } from './maps/sketch.ts';
export { MapArrange } from './maps/arrange.ts';
export { MapScenes, type MapScenesItem, type MapScenesOrder } from './maps/scenes.ts';
export { MapCast } from './maps/cast.ts';
export { MapInk, type MapInkGesture } from './maps/ink.ts';
export { scappleColorInk, scappleIds, scappleImport, scappleSummary, type ScappleReport } from './import/scapple.ts';
export { nextBelow, randomElement, SplitMix64, systemRandom, type RandomSource } from './random.ts';
// Writing history from the app a project came from (Scrivener's Project ▸ Writing History).
export {
  addingDaily, historySourceName, historySummary, importedDaily, importedDayWords, importedHistory, IMPORTED_WORDS_KEY, isHistoryDay,
  recordImportedHistory, SCRIVENER_HISTORY_PATH, SCRIVENER_HISTORY_SOURCE, scrivenerHistoryDays, type HistorySummary, type ImportedDay,
} from './importedHistory.ts';
// Tags: several on one document, kept as text with commas between them.
export { addingTag, fragmentTags, hasTag, joinTags, parseTags, removingTag, tagCounts, tagKey, TAGS_FIELD, type TagCount } from './tags.ts';

// The prose dialect, beyond the contract.
export {
  ALIGNMENTS, INK_COLORS, blockPlainText, escape, escapeLeading, fromPlainText, isNumbered, letters, listStyleOf, marker,
  markdownBefore, parse, parseInlines, parseListMarker, plainParagraphs, plainStyle, plainText, roman, sameStyle, serialize, serializeInlines,
  spanClasses, spanStyle, startsList, styleFromRawValue, styleRawValue, withoutMarks, wordCount, wordCountOfBlocks,
  wordCountOfPlainText, type ListMarker,
  // Verse: `::: verse` fences, a leading tab a step.
  MAX_VERSE_INDENT, VERSE_INDENT_EM, paragraphFence, verseFence, verseLines, type ParagraphFence, type VerseLine,
  // Links: `[words](address)` with a known scheme.
  LINK_SCHEMES, Linked, isLinkAddress, linkAddress, linkScalarRanges, linkSyntaxRanges, linking, links, removingLinks, type LinkRange,
} from './proseMarkdown.ts';

// Fountain, beyond the contract.
export {
  ELEMENT_RAW, classify as classifyFountain, contentRange as fountainContentRange, inlineSpans as fountainInlineSpans,
  isCharacterCue, isSceneHeading, lines as fountainLines, paragraphs as fountainParagraphs, paragraphText as fountainParagraphText,
  plainText as fountainPlainText, runs as fountainRuns, speakers as fountainSpeakers, speakersOfCue as fountainSpeakersOfCue,
  titleKey as fountainTitleKey, titlePage as fountainTitlePage,
  type ClassifiedElement, type ClassifiedLine, type FountainLineDetail, type Paragraph as FountainParagraph, type Run as FountainRun,
} from './fountain.ts';

// Automerge: typed reads, heads, anchors and offsets as the Swift stores them.
export { ROOT, SwiftReader, WrongType, typedTree, type ObjID, type TypedTree } from './automerge/swiftDoc.ts';
export { JoinedMap, joinedAt, joinedPath, newerFirst, objectsAt } from './automerge/joined.ts';
export { decodeHeads, decodeHeadsIn, encodeHeads } from './automerge/heads.ts';
export { anchoredText, currentRange, currentRangeUTF16, suggestionRange, type Anchor } from './automerge/anchors.ts';
export {
  scalarLength, scalarOffsetOfUTF16, scalarOffsetsOfUTF16, sliceScalars, utf16OffsetOfScalar, utf16RangeOfScalars,
} from './automerge/offsets.ts';
export { between as fractionalIndexBetween, sequence as fractionalIndexSequence } from './automerge/fractionalIndex.ts';

// Colours, numbers and the Unicode rules the counts depend on.
export { ENTITY_HUES, entityColorString, entityCSSColor, nextEntityColor, oklchToSRGB, parseRGB, rgbToCSS } from './colors.ts';
export { numberWords, spellOut } from './numberWords.ts';
export {
  charIsLetter, charIsNumber, charIsWhitespace, graphemes, hasPrefix, isCFAlphanumeric, isCFWhitespace, isCFWhitespaceOrNewline, trimCFWhitespace,
  trimCFWhitespaceAndNewlines,
} from './unicode.ts';

// W4: writing (the contract's `editProject`, and the Swift rules behind it).
export { editProject, DocumentEditor, type EditOptions, fragmentDocName, JournalPosts, makeID, metadataScalar, oneLineTitle, storedWordTarget, subtree, writingDay } from './edit/editProject.ts';
export { amValue, sameScalar, Writer, changeTime, type Scalar } from './edit/typed.ts';

// W4d: history (TextDiff, ProjectState, ProjectDiff), find and replace, sprints.
export { difference, inferringMoves, type Change } from './history/collectionDifference.ts';
export { isUnchanged, sentences, textDiff, wordDiff, words as diffWords, type Segment, type TextDiffResult } from './history/textDiff.ts';
export {
  arrangementNamed, checkpointState, compareStates, diffCounts, liveState, netWords, stateFragment,
  type FragmentChange, type ProjectDiff, type ProjectState, type StateFragment, type StructuralChange,
} from './history/projectDiff.ts';
export { escapedPattern, expandTemplate, findMatches, findPattern, InvalidPattern, replaceLabel, spanClassRanges, type FindMatch, type FindQuery } from './findReplace.ts';
export {
  addToLog, averageRate, bestRate, canExtend, extendedSprint, goalMet, isOver, newSprint, plannedEnd, progress, recordSprint, remaining,
  removeFromLog, sprintClock, sprintNet, sprintsIn, sprintSummary, wordsPerMinute, SPRINT_EXTENDABLE_FOR, SPRINT_EXTENSIONS, SPRINT_LOG_LIMIT,
  type WritingSprint,
} from './sprint.ts';

// Epochs: compacted histories adopted, older epochs' edits absorbed (DocumentStore+Epochs, DocumentTree, HistoryCompactor).
export * as epochs from './epochs/index.ts';
export {
  absorbOlder, absorbOlderEpoch, adoptEpoch, compareEpochs, compactFragmentDoc, compactHistory, compactProjectDoc, compactionRecordOf,
  copyAt, epochIDOf, rebaseOnto, sameEpoch, checkpointHeadsIn, reanchor, remapCheckpoints, remapCheckpointProjectHeads, changeHashes,
  splitChanges, readTree, sameTree, writeCompactionRecord, DifferentEpoch, ABSORB_MESSAGE,
  type Absorbed, type Adoption, type AdoptionOutcome, type CompactionRecord, type EpochID, type EpochSide,
} from './epochs/index.ts';

// W6a: what the MCP tools need of the store (DocumentStore operations beyond `ProjectEditor`), quotes, Markdown in.
export {
  StoreEditor, ModelError, CharacterStudy, STUDY_KINDS, STUDY_FOLDER_MARKER, studyKindOf, studyPageOf, type StudyKind, type StudyKindInfo, INBOX_TITLE, BOOK_MATTER_FIELD, alternateSets, bookMatterStableID, manuscriptKind,
  parentOf, suiteJSON, type AgentComment, type AlternateSet, type FieldSpec, type MapScenesDestination, type WrittenComment,
} from './edit/storeEditor.ts';
export { childrenOf, isWithinSubtree, readMetadata } from './edit/editProject.ts';
export { occurrences as quoteOccurrences, nearest as nearestQuote } from './quoteSearch.ts';
export { currentSynopsisRange } from './automerge/anchors.ts';
export { fromCommonMark } from './commonMark.ts';
export { sha256, sha256Hex, stableUUID } from './hashing.ts';
export { markdownReport } from './history/projectDiff.ts';

// W5a: what compile needs of SuiteModel (BookMatter, PublicationDetails/ISBN, MergeTags, the rest of JournalPosts).
export { BookMatter, BOOK_MATTER_CASES, type BookMatterPlacement } from './bookMatter.ts';
export {
  decodePublication, editionFrom, editionLabel, editionPlaceholder, emptyPublication, isbnOf, ISBN, publicationPlaceholders, trimAll,
  EDITIONS, PUBLICATION_PLACEHOLDERS, STANDARD_FICTION, STANDARD_RIGHTS, type Edition, type ISBNCheck, type PublicationDetails,
} from './publication.ts';
export { MergeTags, MERGE_TAG_STYLES, type MergeTagMatch, type MergeTagStyle } from './mergeTags.ts';
export {
  convertingNodes, demotedPosts, firstPostNumberOf, hasOwnPostMatter, journalPosts, journalPostsInTree, movingNodes, nextPostNumber, POST_ONLY_FIELDS,
  postContaining, postNumberProblems, postStart, sectionPlacement, type JournalPostRef,
} from './journal.ts';

// Settings kept as JSON (SettingsJSON.swift): tolerant reads, writes that keep what this build can't read.
export {
  decodeJSON, encodeSwiftJSON, graft, jsonEqual, lossyArray, mergedJSON, putJSON, toJSONValue, unreadable, type JSONDecode, type JSONValue,
} from './settingsJSON.ts';
export { registerFragment, writeArrangement, writeEntity, writeField, writeFragmentKeys } from './edit/storeEditor.ts';

// W7: the rooms' logic, as namespaces (also the ./plan, ./story, ./revise and ./text subpaths): Plan (boards, beats,
// collections, templates, journals, alternates), Story (continuity, astronomy, story times, presence, shape, glossary,
// names), Revise (common mistakes, the style linter, findings, revision mode, heat), and the text primitives they and
// Statistics share (sentences, words, quotation marks, dialogue balance, readability, sentence lengths, word frequency).
export * as plan from './plan/index.ts';
export * as story from './story/index.ts';
export * as revise from './revise/index.ts';
export * as text from './text/index.ts';
export * from './lineEdits.ts';
// W9c: members of a shared project and attribution (also the ./members subpath).
export * as members from './members/index.ts';
export { buildFountain, type Written } from './fountainBuilder.ts';
export {
  isReprise, isSongLine, numberedSongs, songHeading, songOfParagraph, songsByCharacter, songsIn, songTitle, type Song,
} from './songs.ts';

// Look Up (SuiteModel/LookUp.swift): what the project knows about a word in the text, resolved locally in layers.
export {
  lookUpHits, lookUpKey, lookUpNames, lookUpQuery, resolveLookUp, tokenize, wholeWordIndex, wholeWordSnippet,
  type LookUpAnswer, type LookUpHits, type LookUpNote, type LookUpPage, type LookUpQuery, type LookUpScene, type LookUpSources, type LookUpText, type Token,
} from './lookUp.ts';
